/**
 * HTTP Client Service
 *
 * Enterprise-grade axios HTTP client with:
 * - Request/Response interceptors
 * - Automatic token rotation handling
 * - Payload encryption/decryption (when enabled)
 * - Type-safe generic methods
 * - Comprehensive error handling
 * - Development logging
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { encryptService } from './encrypt.service';

// TypeScript interfaces for better type safety
interface HttpClientConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
}

interface ApiError {
  message: string;
  status?: number;
  statusText?: string;
  data?: any;
}

class HttpClientService {
  private axiosInstance: AxiosInstance;
  private baseURL: string;
  // Flag to prevent multiple 401 redirects/toasts
  private isHandling401 = false;

  constructor(config?: Partial<HttpClientConfig>) {
    // Get base URL from window.__env (matches Angular pattern)
    this.baseURL = this.getBaseURL();

    // Create axios instance with configuration
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: config?.timeout || 120000, // 120 second timeout
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    });

    // Setup interceptors
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Get base URL from window.__env (matches Angular EnvService pattern)
   */
  private getBaseURL(): string {
    if (typeof window !== 'undefined' && (window as any).__env) {
      const apiUrl = (window as any).__env.apiUrl;
      if (apiUrl) {
        // apiUrl already contains the full path including /finpro_admin/v1/
        // Just ensure it ends with a slash
        return apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
      }
    }

    // Fallback to current origin (development)
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/finpro_admin/v1/`;
  }

  /**
   * Request Interceptor
   * - Adds Authorization header with token from sessionStorage
   * - Encrypts POST request bodies if encryption is enabled
   * - Logs requests in development mode
   */
  private setupRequestInterceptor(): void {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add auth token if available (matches Angular pattern)
        const token = sessionStorage.getItem('finpro_k_Authorization');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Handle payload encryption for POST requests (matches Angular pattern)
        this.encryptRequestData(config);

        // Development logging
        if (import.meta.env.DEV) {
          console.log('🚀 API Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            fullURL: `${config.baseURL}${config.url}`,
            hasToken: !!token,
            tokenPreview: token ? `${token.substring(0, 20)}...` : null,
            encrypted: config.method?.toLowerCase() === 'post' && encryptService.isEncryptionEnabled(),
            data: config.data,
          });
        }

        return config;
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error('❌ Request Interceptor Error:', error);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Encrypt request data for POST requests if encryption is enabled
   * - Checks if encryption is enabled and keys are available
   * - Encrypts request body and wraps in encryptedData object
   * - Handles errors gracefully by logging and continuing with unencrypted data
   */
  private encryptRequestData(config: AxiosRequestConfig): void {
    if (config.method?.toLowerCase() !== 'post' || !encryptService.isEncryptionEnabled()) {
      return;
    }

    try {
      if (encryptService.hasEncryptionKeys()) {
        // Encrypt the request body
        const encryptedData = encryptService.payloadEncode(JSON.stringify(config.data));
        config.data = { encryptedData };

        if (import.meta.env.DEV) {
          console.log('🔒 Request encrypted:', {
            url: config.url,
            originalDataType: typeof config.data,
          });
        }
      } else {
        console.warn('[HTTP Client] Encryption enabled but keys not available');
      }
    } catch (error) {
      console.error('[HTTP Client] Failed to encrypt request:', error);
      // Continue with unencrypted data on encryption failure
    }
  }

  /**
   * Response Interceptor
   * - Captures new tokens from 'session-token' header (token rotation)
   * - Decrypts response bodies if encryption is enabled
   * - Logs responses in development mode
   * - Handles errors consistently
   */
  private setupResponseInterceptor(): void {
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // CRITICAL: Token Rotation Handling
        // Backend returns new/refreshed token in 'session-token' header with each request
        // This matches Angular's HttpClient behavior
        const newToken = response.headers['session-token'];
        if (newToken) {
          const oldToken = sessionStorage.getItem('finpro_k_Authorization');
          sessionStorage.setItem('finpro_k_Authorization', newToken);

          if (import.meta.env.DEV) {
            console.log('🔄 Token Rotation Detected:', {
              url: response.config.url,
              oldTokenPreview: oldToken ? `${oldToken.substring(0, 20)}...` : null,
              newTokenPreview: `${newToken.substring(0, 20)}...`,
              tokensMatch: oldToken === newToken,
            });
          }
        }

        // Handle response decryption (matches Angular pattern)
        this.decryptResponseData(response);

        // Development logging
        if (import.meta.env.DEV) {
          console.log('✅ API Response:', {
            url: response.config.url,
            status: response.status,
            statusText: response.statusText,
            tokenRotated: !!newToken,
            decrypted: encryptService.isEncryptionEnabled() && !!response.data?.encryptedData,
            data: response.data,
          });
        }

        return response;
      },
      (error: AxiosError) => {
        return this.handleError(error);
      }
    );
  }

  /**
   * Decrypt response data if encryption is enabled
   * - Checks for encrypted data in response
   * - Retrieves decryption key from sessionStorage
   * - Decrypts and replaces response.data with decrypted object
   * - Handles errors gracefully by logging and returning original response
   */
  private decryptResponseData(response: AxiosResponse): void {
    if (!encryptService.isEncryptionEnabled()) {
      return;
    }

    try {
      const encrypted = response.data?.encryptedData;
      const key = sessionStorage.getItem('aainfokey');

      if (encrypted && key) {
        // Decrypt the response
        const decrypted = encryptService.decryptUsingAES256(encrypted, key);
        const decryptedObject = JSON.parse(decrypted);
        response.data = decryptedObject;

        if (import.meta.env.DEV) {
          console.log('🔓 Response decrypted:', {
            url: response.config.url,
          });
        }
      } else if (encrypted && !key) {
        console.warn('[HTTP Client] Encrypted response but decryption key not available');
      }
      // If no encrypted data, response.data is already plain
    } catch (error) {
      console.error('[HTTP Client] Failed to decrypt response:', error);
      // Return original response on decryption failure
    }
  }

  /**
   * Centralized error handling
   * Matches Angular's error handling pattern
   */
  private handleError(error: AxiosError): Promise<ApiError> {
    const apiError: ApiError = {
      message: 'An unexpected error occurred',
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
    };

    if (error.response) {
      // Server responded with error status
      const errorData = error.response.data as any;
      apiError.message = errorData?.message || errorData?.error || error.message;

      if (import.meta.env.DEV) {
        console.error('❌ API Error Response:', {
          url: error.config?.url,
          status: error.response.status,
          statusText: error.response.statusText,
          message: apiError.message,
          data: error.response.data,
        });
      }

      // Handle 401 Unauthorized - Session expired
      // Redirect to login page (matches Angular behavior)
      // Note: Don't override apiError.message here - let the hasBackendMessage check below handle it
      // This preserves backend messages like "Invalid credentials" for login endpoint 401s
      if (error.response.status === 401) {
        // If already handling 401, suppress this error to prevent confusing messages
        // during the redirect window (in-flight requests will fail after token is cleared)
        if (this.isHandling401) {
          if (import.meta.env.DEV) {
            console.log('[HTTP Client] Suppressing subsequent 401 - redirect pending');
          }
          // Return a suppressed error that won't show confusing messages to user
          return Promise.reject({
            message: 'Session expired - redirect pending',
            status: 401,
            suppressed: true,
          });
        }
        this.handleUnauthorized(error.config?.url);
      }

      // Handle specific error cases (matches Angular)
      // Only use generic messages if backend didn't provide a specific message
      const hasBackendMessage = errorData?.message || errorData?.error;

      if (!hasBackendMessage) {
        if (error.response.status === 401) {
          apiError.message = 'Session expired. Please login again.';
        } else if (error.response.status === 403) {
          apiError.message = 'Access denied. Please contact your administrator.';
        } else if (error.response.status === 500) {
          apiError.message = 'Server error. Please try again later.';
        }
      }
    } else if (error.request) {
      // Request made but no response
      apiError.message = 'No response from server. Please check your connection.';

      if (import.meta.env.DEV) {
        console.error('❌ API No Response:', {
          url: error.config?.url,
          message: error.message,
        });
      }
    } else {
      // Something else happened
      apiError.message = error.message;

      if (import.meta.env.DEV) {
        console.error('❌ API Request Setup Error:', error.message);
      }
    }

    return Promise.reject(apiError);
  }

  /**
   * Check if currently on login page
   */
  private isOnLoginPage(): boolean {
    const hash = window.location.hash;
    return hash.includes('/login') ||
           hash.includes('/site') ||
           hash.includes('/forgot-password');
  }

  /**
   * Handle 401 Unauthorized errors
   * - Clears invalid session data from sessionStorage
   * - Redirects to login page via Angular bridge or directly
   * - Skips redirect for login-related API calls to avoid loops
   * - Prevents multiple toasts/redirects using a flag
   */
  private handleUnauthorized(requestUrl?: string): void {
    // Skip redirect for login-related endpoints to avoid infinite loops
    const loginEndpoints = ['user/login', 'user/verify', 'browser/info', 'user/forgotpassword'];
    const isLoginRequest = loginEndpoints.some(endpoint => requestUrl?.includes(endpoint));

    if (isLoginRequest) {
      if (import.meta.env.DEV) {
        console.log('[HTTP Client] 401 on login endpoint - skipping redirect');
      }
      return;
    }

    // Skip if already handling 401 or already on login page
    if (this.isHandling401 || this.isOnLoginPage()) {
      if (import.meta.env.DEV) {
        console.log('[HTTP Client] 401 - skipping (already handling or on login page)');
      }
      return;
    }

    // Set flag to prevent multiple toasts/redirects
    this.isHandling401 = true;

    if (import.meta.env.DEV) {
      console.warn('[HTTP Client] 401 Unauthorized - Session expired, redirecting to login');
    }

    // Clear invalid session data (matches Angular behavior)
    sessionStorage.removeItem('finpro_k_Authorization');
    sessionStorage.removeItem('loggedUserInfo');

    // Redirect to login page after a short delay (matches Angular's 2s delay)
    setTimeout(() => {
      // Try Angular bridge first (preferred for proper Angular routing)
      if (window.AngularAuthBridge?.handleSessionExpired) {
        window.AngularAuthBridge.handleSessionExpired();
      } else {
        // Fallback: Direct redirect using hash-based routing
        const basePath = window.location.pathname;
        const newUI = sessionStorage.getItem('isReactLoginEnabled') === 'true';
        const targetUrl = basePath + (newUI ? '#/login' : '#/site');
        window.location.href = targetUrl;
      }
    }, 2000);

    // Reset flag after a longer cooldown (5s) to prevent race conditions
    // Navigation is async, so we need to wait longer before allowing new 401 handling
    setTimeout(() => {
      this.isHandling401 = false;
    }, 5000);
  }

  /**
   * Generic GET request
   */
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  /**
   * Generic POST request
   */
  public async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  /**
   * Generic PUT request
   */
  public async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  /**
   * Generic DELETE request
   */
  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  /**
   * Generic PATCH request
   */
  public async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  /**
   * Get the base URL being used
   */
  public getBaseUrl(): string {
    return this.baseURL;
  }
}

// Export singleton instance (matches Angular service pattern)
export const httpClient = new HttpClientService();

// Export class for testing or custom instances
export { HttpClientService };
export type { HttpClientConfig, ApiError };
